/**
 * The page's opening statement: where you are, what this screen is, what it is measured
 * over, and the one thing you came here to do.
 *
 * ## What was there before
 *
 * `src/app/routes/router.tsx` rendered an `<h1>` and nothing else. Four facts a planner
 * needs before reading a single number were either absent or scattered:
 *
 * | fact | where it was |
 * |---|---|
 * | which chapter this screen belongs to | the breadcrumb, in the header, in 13px secondary ink |
 * | what the screen concluded | nowhere — the title is two or three words |
 * | what perimeter the numbers are over | inside the screen, in a different place on each one |
 * | the screen's primary action | inside the screen, usually at the bottom |
 *
 * The third is the one that matters. reqs/07 is built on the rule that a number states its
 * perimeter; a screen that renders its scope halfway down, differently from its neighbour,
 * satisfies that rule and defeats its purpose. Putting it in one fixed place is not
 * decoration.
 *
 * ## The kicker is the chapter, and the router supplies it
 *
 * A screen does not name its own chapter. The router resolves it from the feature registry,
 * because the chapter a route belongs to is a property of the manifest and a screen that
 * restated it could disagree with navigation.
 *
 * ## There is no explanatory line under the title
 *
 * The block is kicker, title, then the screen's headline sentence if it has one (owner
 * ruling, 2026-09-30, ADR 9031). A one-line "what this screen is for" used to sit between
 * title and headline; it was removed everywhere, not hidden, so no route can bring it back.
 * The paragraphs a planner reads once live in the Documentation screen (`docSectionKey`).
 */

import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface PageHeaderProps {
  /**
   * The chapter this screen belongs to. Supplied by the router from the manifest, never by
   * the screen.
   */
  readonly kickerKey?: string;
  /**
   * The kicker already in words, when the SCREEN's place is narrower than its chapter: «Prezzi ·
   * Parametri» on a screen four apps open with their own scope (brief part 1 §2, part 2 §5 «App ·
   * Pagina»). Set through `<PageKicker>`; wins over `kickerKey`.
   */
  readonly kicker?: string;
  readonly titleKey: string;
  /**
   * What the numbers on this screen are measured over: country, season, run, as-of date.
   * A node rather than a key, because a perimeter is usually several resolved values and
   * some of them are chips a planner can act on.
   */
  readonly perimeter?: ReactNode;
  /** The screen's primary action, if it has exactly one. */
  readonly action?: ReactNode;
  /**
   * Rendered under the headline, above the perimeter — the place a screen states something
   * standing about itself that is not its subject — the router puts the terminal-drill
   * notice here. Never the manifest's `drill.reason`, which is written for a reviewer.
   */
  readonly notice?: ReactNode;
  /**
   * The screen's conclusion, under the title (spec v6b §3, ADR 9013): the slot a screen's
   * `<Headline>` portals into. The router supplies the node; the screen supplies the sentence.
   */
  readonly headline?: ReactNode;
  /**
   * The way into the screen's guide — «Guided tour» and «Read the guide» (ADR 9044). Supplied by
   * the router for a route that has a guide, never by the screen. Drawn at the inline end, above
   * the action, so it never pushes the title.
   */
  readonly guide?: ReactNode;
}

export function PageHeader({
  kickerKey, kicker, titleKey, perimeter, action, notice, headline, guide,
}: PageHeaderProps): React.JSX.Element {
  const { t } = useTranslation();
  const kickerText = kicker ?? (kickerKey === undefined ? undefined : t(kickerKey));
  return (
    // The rule runs the page's width; the block inside it keeps the content box's edges — the
    // rail plus `page.margin`, and 1760px at most, left-aligned (brief part 3 §B1, ADR 9038).
    // `data-page-header`: a «Documento» page prints its own header, so this one is hidden in print
    // there (ADR 9053, `src/styles/index.css`).
    <div className="border-hairline border-b pb-4 pt-6" data-page-header="">
      <div className="mx-page flex max-w-content flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-2">
        {kickerText !== undefined && (
          <p className="text-micro text-ink-secondary" data-page-kicker="">{kickerText}</p>
        )}
        {/* §4.2 — `type-display` is the page title and there is ONE per screen. */}
        <h1 className="text-display text-ink-primary font-display uppercase">{t(titleKey)}</h1>
        {headline}
        {notice}
        {perimeter}
      </div>
      {guide === undefined ? action : (
        <div className="flex shrink-0 flex-col items-end gap-2">
          {guide}
          {action}
        </div>
      )}
      </div>
    </div>
  );
}

// ── the kicker a screen narrows ──────────────────────────────────────────────

const PageKickerSetter = createContext<((kicker: string | null) => void) | null>(null);

/** Provided by the router, with the setter of the header's kicker override. */
export const PageKickerProvider = PageKickerSetter.Provider;

/**
 * Narrows the header's kicker from the chapter to the screen's own place — «Prezzi · Parametri»
 * (brief part 2 §5: the first element is «App · Pagina»). The chapter stays the default and the
 * router's; a screen that is ONE place in the rail keeps it. Cleared when the screen unmounts.
 * Outside a routed page — a test — it does nothing.
 */
export function PageKicker({ text }: { readonly text: string }): null {
  const set = useContext(PageKickerSetter);
  useEffect(() => {
    if (set === null) return undefined;
    set(text);
    return () => set(null);
  }, [set, text]);
  return null;
}
